'use client'

import { cva, VariantProps } from 'class-variance-authority'
import {
  type ComponentProps,
  useImperativeHandle,
  type MouseEvent,
  type FocusEvent,
  type ChangeEvent,
  RefObject,
} from 'react'

import { cn, createSlot, useFocus } from '@/shared/lib'

export const field = cva(
  [
    'typo-label1 flex items-center gap-2 overflow-hidden rounded-sm border border-gray-100 bg-white transition-all',
    'placeholder:text-gray-400',
  ],
  {
    variants: {
      size: {
        small: 'h-10',
        default: 'h-12',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-fit',
      },
      error: {
        true: 'border-red-500 caret-red-500',
        false: '',
      },
      disabled: {
        true: 'cursor-default bg-gray-50 text-gray-400',
        false: '',
      },
      focused: {
        true: 'border-black focus-visible:outline-none',
        false: '',
      },
      readOnly: {
        true: 'cursor-default bg-gray-50',
        false: '',
      },
    },

    compoundVariants: [
      {
        error: true,
        focused: true,
        class: 'border-red-500 caret-red-500',
      },
    ],
  },
)

export const input = cva(
  'peer h-full w-full bg-transparent px-[11px] placeholder:text-gray-400 disabled:pointer-events-none',
)

export type InputVariants = VariantProps<typeof field>

export type Props = Omit<ComponentProps<'input'>, 'size'> &
  InputVariants & {
    padding?: {
      x?: number
      y?: number
    }
    error?: boolean
    wrapperRef?: RefObject<HTMLDivElement | null>
  }

export const Input = ({
  type: typeProp = 'text',
  value = '',
  className,
  size = 'default',
  fullWidth = false,
  error: errorProp = false,
  readOnly: readOnlyProp = false,
  disabled: disabledProp = false,
  maxLength = 255,
  children,
  autoComplete = 'off',
  onClick,
  onFocus,
  onBlur,
  ref,
  wrapperRef,
  onChange,
  padding: paddingProp,
  ...restProps
}: Props) => {
  // number 타입은 text 타입으로 지정 후 핸들러에서 숫자만 허용하도록 함
  const type = typeProp === 'number' ? 'text' : typeProp
  const readOnly = readOnlyProp || Boolean(restProps['aria-readonly'])
  const disabled = disabledProp || Boolean(restProps['aria-disabled'])
  const error = errorProp || Boolean(restProps['aria-invalid'])

  const { slots, hasSlot } = createSlot(children, ['left', 'right'])

  const slotClass = (() => {
    if (hasSlot('left') && hasSlot('right')) {
      return 'both'
    }

    if (hasSlot('left')) {
      return 'left'
    }

    if (hasSlot('right')) {
      return 'right'
    }

    return null
  })()

  const padding = {
    x: 11,
    y: size === 'small' ? 9 : 11,
    ...paddingProp,
  }

  const {
    focused,
    elementRef,
    handleElementClick,
    handleElementFocus,
    handleElementBlur,
  } = useFocus<HTMLInputElement>()

  useImperativeHandle(ref, () => elementRef.current as HTMLInputElement)

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    onClick?.(event)
    handleElementClick()
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (readOnly || disabled) return

    onFocus?.(event)
    handleElementFocus()
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event)
    handleElementBlur()
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (typeProp === 'number' || typeProp === 'tel') {
      const notNumber = /[^0-9]/g
      const value = (event.nativeEvent as InputEvent).data

      if (value && notNumber.test(value)) {
        return
      }
    }

    onChange?.(event)
  }

  return (
    <div
      ref={wrapperRef}
      className={cn(
        field({
          size,
          fullWidth,
          error,
          disabled,
          focused,
          readOnly,
          className,
        }),
      )}
      onClick={handleClick}
    >
      {slots?.left && (
        <div
          className="flex"
          style={{
            paddingLeft: padding.x,
            paddingTop: padding.y,
            paddingBottom: padding.y,
          }}
        >
          {slots.left}
        </div>
      )}
      <input
        {...restProps}
        type={type}
        value={value}
        className="peer h-full w-full bg-transparent placeholder:text-gray-400 disabled:pointer-events-none"
        autoComplete={autoComplete}
        readOnly={readOnly}
        disabled={disabled}
        maxLength={maxLength}
        ref={elementRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        style={{
          paddingLeft: padding.x,
          paddingRight: padding.x,
          paddingTop: padding.y,
          paddingBottom: padding.y,
          ...(slotClass === 'both' ? { paddingLeft: 0, paddingRight: 0 } : {}),
          ...(slotClass === 'left' ? { paddingLeft: 0 } : {}),
          ...(slotClass === 'right' ? { paddingRight: 0 } : {}),
        }}
      />
      {slots?.right && (
        <div
          className="flex"
          style={{
            paddingRight: padding.x,
            paddingTop: padding.y,
            paddingBottom: padding.y,
          }}
        >
          {slots.right}
        </div>
      )}
    </div>
  )
}
